import { useMemo } from 'react';
import { TranscriptPauseEntry } from './types';
import { parseTranscriptToParagraphs } from './utils';
import { hasDeleteAtPause, isPauseInvalidForOptimization } from './pauseUtils';
import { useTranscriptContext } from '@/context/TranscriptContext/TranscriptContext';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { getAllSegments, isClipInOriginalLanguage } from '@/player-config/src/utils/clipUtils';
import { SegmentType } from '@/player-config/src/types/Segment/Type';
import { getDeletesForSegment } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/playerConfigUtil';
import { getCaptionsForSegment } from '@/player-config/src/utils/playerConfigUtils';
import type { ClipDeletes } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import type { Word } from '@/player-config/src/types/transcript-types';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

interface ClipPauseEntries {
  unoptimized: TranscriptPauseEntry[];
  optimized: TranscriptPauseEntry[];
  invalid: TranscriptPauseEntry[];
  deletesPerSegment: Record<string, ClipDeletes>;
}

export default function useClipPauseEntries(deletesBySegment?: Record<string, ClipDeletes>): ClipPauseEntries {
  const transcriptStore = useTranscriptContext();
  const { clipData } = useClipsContext();

  const allSegments = useMemo(() => getAllSegments(clipData), [clipData]);

  const isTranslated = useMemo(() => !isClipInOriginalLanguage(clipData), [clipData]);

  const transcript = useMemo(() => {
    if (transcriptStore.transcript.length > 0 && isTranslated) {
      return allSegments.reduce(
        (acc, segment) => {
          if (segment.type !== SegmentType.COMPOSITE) {
            return { ...acc, [segment.id]: [] };
          }
          return {
            ...acc,
            [segment.id]:
              getCaptionsForSegment({
                clip: clipData,
                segmentId: segment.id,
                isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
              }) ?? []
          };
        },
        {} as Record<string, Word[]>
      );
    }
    return allSegments.reduce(
      (acc, segment) => {
        if (segment.type !== SegmentType.COMPOSITE) {
          return { ...acc, [segment.id]: [] };
        }
        return { ...acc, [segment.id]: transcriptStore.transcript };
      },
      {} as Record<string, Word[]>
    );
  }, [transcriptStore.transcript, isTranslated, allSegments, clipData]);

  const pauseEntries = useMemo(() => {
    if (!transcriptStore.isTranscriptInitialized) {
      return [];
    }

    const paragraphs = parseTranscriptToParagraphs(
      transcript,
      transcriptStore.speakersWithDetails,
      clipData,
      transcriptStore.chapterData ?? [],
      isTranslated
    );

    return paragraphs.flatMap(p => p.pauseEntries ?? []);
  }, [
    transcript,
    transcriptStore.isTranscriptInitialized,
    transcriptStore.speakersWithDetails,
    transcriptStore.chapterData,
    clipData,
    isTranslated
  ]);

  const deletesPerSegment = useMemo(() => {
    return allSegments.reduce(
      (acc, segment) => {
        if (segment.type !== SegmentType.COMPOSITE) {
          return acc;
        }
        return { ...acc, [segment.id]: getDeletesForSegment(clipData, segment.id) };
      },
      {} as Record<string, ClipDeletes>
    );
  }, [allSegments, clipData]);

  const activeDeletesPerSegment = deletesBySegment ?? deletesPerSegment;

  const { unoptimized, optimized, invalid } = useMemo(() => {
    const unoptimized: TranscriptPauseEntry[] = [];
    const optimized: TranscriptPauseEntry[] = [];
    const invalid: TranscriptPauseEntry[] = [];

    for (const entry of pauseEntries) {
      const segmentDeletes = activeDeletesPerSegment[entry.segmentId];

      if (isPauseInvalidForOptimization(entry, segmentDeletes)) {
        invalid.push(entry);
        continue;
      }

      const isOptimized = hasDeleteAtPause(entry, segmentDeletes);

      if (isOptimized) {
        optimized.push(entry);
      } else {
        unoptimized.push(entry);
      }
    }

    return { unoptimized, optimized, invalid };
  }, [pauseEntries, activeDeletesPerSegment]);

  return { unoptimized, optimized, invalid, deletesPerSegment };
}
